import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";

export const fetchCompanies = createAsyncThunk(
  "company/fetchCompanies",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    if (search) params.append("search", search.trim());
    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    const filterPayload = FilterBuilder(filters ?? []);

    const body = {
      filters: filterPayload,
    };

    const response: any = await Axios.post(
      `${API_ENDPOINT?.MASTERS?.CUSTOMER?.LIST}?${params.toString()}`,
      body,
    );
    return {
      data: response.data.data || [],
      rowCount: response.data.count || 0,
    };
  },
);

export const fetchCompanyById = createAsyncThunk(
  "company/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `${API_ENDPOINT.MASTERS?.CUSTOMER?.DETAIL(id)}`,
      );
      return response?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Company by ID",
      );
    }
  },
);

export const fetchCompanyByDropDown = createAsyncThunk(
  "company/fetchByDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT.MASTERS?.CUSTOMER?.DROPDOWN}`,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Company list",
      );
    }
  },
);

export const updateCompany = createAsyncThunk(
  "company/updateCompany",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.put(
        `${API_ENDPOINT.MASTERS?.CUSTOMER?.UPDATE(data?.id)}`,
        data,
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to update Company",
      );
    }
  },
);

export const updateStatusInCompany = createAsyncThunk(
  "company/updateStatusInCompany",
  async (data: any, { rejectWithValue }) => {
    try {
      const payload = { is_active: data.is_active };
      const response: any = await Axios.patch(
        `${API_ENDPOINT.MASTERS?.CUSTOMER?.STATUS_UPDATE(data?.id)}`,
        payload,
      );
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update status in Company",
      );
    }
  },
);

export const addCompany = createAsyncThunk(
  "category/addCompany",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        `${API_ENDPOINT.MASTERS?.CUSTOMER?.CREATE}`,
        data,
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to add customer",
      );
    }
  },
);

export const deleteCompany = createAsyncThunk(
  "company/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      await Axios.delete(`${API_ENDPOINT.MASTERS?.CUSTOMER?.DELETE(id)}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const exportCompanyCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: any;
}) => {
  const params = new URLSearchParams();

  if (search) params.append("search", search);
  const filterPayload = FilterBuilder(filters ?? []);

  const body = {
    filters: filterPayload,
  };
  const response: any = await Axios.post(
    `${API_ENDPOINT?.MASTERS?.CUSTOMER?.EXPORT}?${params.toString()}`,
    body,
    {
      responseType: "blob",
    },
  );

  return {
    blob: response.data,
    filename: `${
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "Company List"
    }_${new Date().toISOString().split("T")[0]}.csv`,
  };
};

export const exportCompanyTallyCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: any;
}) => {
  const params = new URLSearchParams();

  if (search) params.append("search", search);
  const filterPayload = FilterBuilder(filters ?? []);

  const body = {
    filters: filterPayload,
  };

  const response: any = await Axios.post(
    `${API_ENDPOINT?.MASTERS?.CUSTOMER?.EXPORT_TALLY}?${params.toString()}`,
    body,
    {
      responseType: "blob",
    },
  );

  return {
    blob: response.data,
    filename: `${
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "companies_tally"
    }_${new Date().toISOString().split("T")[0]}.csv`,
  };
};
